import React from 'react';
import { Checkbox } from 'new/elements/Checkbox/Checkbox';
import { Box } from '@mui/material';
import type { SxProps, Theme } from '@mui/system';
import UIUtils from 'js/lib/UIUtils';
import omit from 'lodash/omit';

interface RawFileDataCheckboxProps {
  section: string;
  sourceConfig: Record<string, any>;
  sourceConfigFields: any[];
  onSourceConfigChanged: (newConfig: Record<string, any>) => void;
  marginLeft?: number;
  checkboxSx?: SxProps<Theme>;
}

/**
 * Helper to determine if skip file parsing is enabled.
 * Derives the value from advanced_settings - no need to store a separate property.
 */
export const isSkipFileParsing = (sourceConfig: Record<string, any> | undefined): boolean => {
  return sourceConfig?.advanced_settings === 'Raw File Data';
};

const RawFileDataCheckbox: React.FC<RawFileDataCheckboxProps> = ({
  section,
  sourceConfig,
  sourceConfigFields,
  onSourceConfigChanged,
  marginLeft = 2,
  checkboxSx,
}) => {
  // Derive checkbox state directly from advanced_settings
  const isChecked = isSkipFileParsing(sourceConfig);

  if (section !== 'Define Processing Logic') {
    return null;
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    const newValue = checked ? 'Raw File Data' : 'Auto Detect';

    // Update advanced_settings exactly as PopUpForm._handleFieldUpdated does
    let newData = { ...sourceConfig, advanced_settings: newValue };

    // Add missing defaults for conditional fields
    newData = UIUtils.configWithMissingDefaults(newData, sourceConfigFields);

    // Two-pass filtering to remove invisible fields (same as PopUpForm)
    let invisibleFieldNames = sourceConfigFields
      .filter((field: any) => !UIUtils.shouldRenderField(field, newData))
      .map((field: any) => field.name);
    let visibleFieldData = omit(newData, invisibleFieldNames);

    invisibleFieldNames = sourceConfigFields
      .filter((field: any) => !UIUtils.shouldRenderField(field, visibleFieldData))
      .map((field: any) => field.name);
    visibleFieldData = omit(visibleFieldData, invisibleFieldNames);

    // Pass to onSourceConfigChanged which will call reduceConfig for flow-type filtering
    onSourceConfigChanged(visibleFieldData);
  };

  return (
    <Box marginTop={1} marginLeft={marginLeft}>
      <Checkbox
        checked={isChecked}
        onChange={handleChange}
        label="Skip File Parsing"
        labelPlacement="end"
        sx={checkboxSx}
      />
    </Box>
  );
};

export default RawFileDataCheckbox;
